import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, keyframes } from "@mui/material";
import { buildSessionStatusStyles, formatRelativeTime } from "./agentSessionsPanelUtils";
import { useStatusPalette } from "../../utils/statusColors";

const slideIn = keyframes`
  from {
    opacity: 0;
    max-height: 0;
    transform: translateX(-8px);
  }
  to {
    opacity: 1;
    max-height: 80px;
    transform: translateX(0);
  }
`;

const statusPulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.15); }
  100% { transform: scale(1); }
`;

function StatusBadge({ status, animate }) {
  const _sp = useStatusPalette();
  const STATUS_STYLES = buildSessionStatusStyles(_sp);
  const s = STATUS_STYLES[status] || STATUS_STYLES.IN_PROGRESS;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        height: "20px",
        px: "6px",
        borderRadius: "5px",
        backgroundColor: s.bg,
        border: `1px solid ${s.border}`,
        transition: "background-color 300ms ease, border-color 300ms ease",
        ...(animate && {
          animation: `${statusPulse} 350ms ease-out`,
        }),
      }}
    >
      <Box
        sx={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          backgroundColor: s.color,
          flexShrink: 0,
          transition: "background-color 300ms ease",
        }}
      />
      <Typography
        sx={{
          fontSize: "11px",
          fontWeight: 500,
          color: s.color,
          lineHeight: 1,
          whiteSpace: "nowrap",
          transition: "color 300ms ease",
        }}
      >
        {s.label}
      </Typography>
    </Box>
  );
}

export { StatusBadge };

export default function SessionRow({
  session,
  selected,
  onClick,
  showWorkflowProvenanceInChat = true,
}) {
  const preview = session.title || "Untitled session";
  const isNew = useRef(true);
  const prevStatusRef = useRef(session.status);
  const [statusAnimating, setStatusAnimating] = useState(false);

  useEffect(() => {
    isNew.current = false;
  }, []);

  useEffect(() => {
    if (prevStatusRef.current !== session.status) {
      prevStatusRef.current = session.status;
      setStatusAnimating(true);
      const timer = setTimeout(() => setStatusAnimating(false), 400);
      return () => clearTimeout(timer);
    }
  }, [session.status]);

  return (
    <Box
      onClick={onClick}
      sx={{
        px: 2,
        py: 1.5,
        cursor: "pointer",
        backgroundColor: selected ? "background.paper" : "transparent",
        borderLeft: selected ? "3px solid" : "3px solid transparent",
        borderLeftColor: selected ? "text.primary" : "transparent",
        transition: "background-color 150ms ease, border-color 150ms ease",
        "&:hover": { backgroundColor: selected ? "background.paper" : "action.hover" },
        ...(isNew.current && {
          animation: `${slideIn} 300ms ease-out both`,
        }),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: selected ? 600 : 500,
            color: "text.primary",
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {preview}
        </Typography>
        <Typography sx={{ fontSize: "11px", color: "text.secondary", flexShrink: 0 }}>
          {formatRelativeTime(session.createdAt)}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <StatusBadge status={session.status} animate={statusAnimating} />
        {showWorkflowProvenanceInChat &&
          session.source === "WORKFLOW_INVOCATION" && (
            <>
              <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
                ·
              </Typography>
              <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
                Workflow
              </Typography>
            </>
          )}
      </Box>
    </Box>
  );
}
